import { useEffect, useRef } from 'react';
import {
  CanvasSource,
  CanvasSourceOptions,
  CanvasSourceRaw,
  GeoJSONSource,
  GeoJSONSourceOptions,
  GeoJSONSourceRaw,
  ImageSource,
  ImageSourceOptions,
  ImageSourceRaw,
  RasterDemSource,
  RasterSource,
  VectorSource,
  VectorSourceImpl,
  VideoSource,
  VideoSourceOptions,
  VideoSourceRaw
} from 'mapbox-gl';

import { useMap } from './useMap';
import { useRerenderOnMapStyleLoad } from './useRerenderOnMapStyleLoad';
import {
  updateData,
  updateImage,
  updateTiles,
  updateUrl,
  updateCoordinates
} from './Source.helpers';
import { useSourceLoadingHandlers } from './useSourceLoadingHandlers';

export type AnySourceData =
  | GeoJSONSourceRaw
  | VideoSourceRaw
  | ImageSourceRaw
  | CanvasSourceRaw
  | VectorSource
  | RasterSource
  | RasterDemSource;

// Once the following PR is merged and new version of @types/mapbox-gl is released we can remove this
// https://github.com/DefinitelyTyped/DefinitelyTyped/pull/68224
export interface RasterSourceImpl extends RasterSource {
  setTiles(tiles: readonly string[]): RasterSourceImpl;
  setUrl(url: string): RasterSourceImpl;
}

export type AnySourceImpl =
  | GeoJSONSource
  | VideoSource
  | ImageSource
  | CanvasSource
  | VectorSourceImpl
  | RasterSourceImpl
  | RasterDemSource;

export type SourceWithTiles = VectorSource | RasterSource;
export type SourceImplWithTiles = VectorSourceImpl | RasterSourceImpl;
export type SourceWithCoordinates = ImageSource | VideoSource | CanvasSource;
export type SourceOptionsWithCoordinates =
  | ImageSourceOptions
  | VideoSourceOptions
  | CanvasSourceOptions;

export type SourceProps = AnySourceData & {
  /**
   * Unique ID for the source
   */
  id: string;

  /**
   * Layers that need to be associated with this source
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  layers?: ({ sourceId }: { sourceId: string }) => any;

  /**
   * called when the source starts loading
   */
  onLoadStart?: () => void;

  /**
   * called when the map enters an idle state after loading
   */
  onLoadFinish?: () => void;
};

export const Source = ({
  id: idProp,
  layers,
  onLoadStart,
  onLoadFinish,
  ...props
}: SourceProps) => {
  const id = idProp;

  // This ref is used to compare props on each render to determine what needs to be
  // updated, we are not using useEffect or other React hooks to manage source updates
  // because mapbox-gl has it's own update lifecycle so we delegate all update to
  // mapbox-gl and do not try to control it with React lifecycle methods
  const prevPropsRef = useRef(props);

  const map = useMap();
  //
  // Triggers a re-render when map style changes so we can add back the source
  //
  useRerenderOnMapStyleLoad();

  useSourceLoadingHandlers({ id, onLoadStart, onLoadFinish });

  useEffect(() => {
    return () => {
      // The Map interface does not have a style property, but the Mapbox GL JS map
      // object does.
      //
      // @ts-expect-error
      if (map && Boolean(map.style) && map.getSource(id)) {
        // We need to make sure that all layers that are using this source is removed
        // before we try and remove the source, otherwise mapbox-gl will throw an error
        const mapLayers = map.getStyle()?.layers ?? [];
        for (const layer of mapLayers) {
          // @ts-expect-error "source" is not present on all layers...
          if (layer?.source === id) {
            map.removeLayer(layer.id);
          }
        }

        map.removeSource(id);
      }
    };
  }, [map, id]);

  /**
   * Make sure the style is loaded before trying to add the source to the map as adding
   * source or layer to the map while the styles is not loaded will throw an error
   */
  // @ts-expect-error accessing private property
  if (!map || !map.style?._loaded) {
    return null;
  }

  /**
   * Handle adding source
   */
  const hasSource = !!map?.getSource(id);
  if (!hasSource) {
    map?.addSource(id, props as AnySourceData);
    return layers?.({ sourceId: id }) ?? null;
  }

  /**
   * Handle updating source
   */
  if (props.type !== prevPropsRef.current.type) {
    // TODO: do we want to throw an error here?
    console.error(
      'Changing source `type` after source is added is not supported'
    );
  }

  const source = map?.getSource(id);

  if (source && props.type === 'geojson') {
    updateData(
      source as GeoJSONSource,
      (props as GeoJSONSourceOptions).data,
      (prevPropsRef.current as GeoJSONSourceOptions).data
    );
  }

  if (source && (props.type === 'vector' || props.type === 'raster')) {
    updateTiles(
      source as SourceImplWithTiles,
      (props as SourceWithTiles).tiles,
      (prevPropsRef.current as SourceWithTiles).tiles
    );
    updateUrl(
      source as SourceImplWithTiles,
      (props as SourceWithTiles).url,
      (prevPropsRef.current as SourceWithTiles).url
    );
  }

  if (source && props.type === 'image') {
    updateImage(
      source as ImageSource,
      props as ImageSourceOptions,
      prevPropsRef.current as ImageSourceOptions
    );
  }

  if (
    source &&
    (props.type === 'canvas' ||
      props.type === 'image' ||
      props.type === 'video')
  ) {
    updateCoordinates(
      source as SourceWithCoordinates,
      (props as SourceOptionsWithCoordinates).coordinates,
      (prevPropsRef.current as SourceOptionsWithCoordinates).coordinates
    );
  }

  prevPropsRef.current = props;

  return layers?.({ sourceId: id }) ?? null;
};

export default Source;
